/** A contact's issues, on their profile. */
import { useQuery } from '@apollo/client';
import { Badge, Button, Card, EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { Plus } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IssueDialog } from '../issues/IssueDialog.web.jsx';
import { formatDate } from '../../lib/format.js';
import {
  ISSUE_PRIORITY_LABELS,
  ISSUE_STATUS_LABELS,
  PRIORITY_CLASS,
  STATUS_CLASS,
} from '../../lib/issueLabels.js';
import { ISSUES_QUERY } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

export function IssuesPanel({ contact }) {
  const [creating, setCreating] = useState(false);
  const { data, loading, refetch } = useQuery(ISSUES_QUERY, {
    variables: { page: 1, pageSize: 20, contactId: contact.id },
    errorPolicy: 'ignore',
  });
  const issues = data?.issues?.items ?? [];

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 className="text-md font-bold">{S.issues.title}</h2>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus size={13} aria-hidden="true" />
          {S.issues.newIssue}
        </Button>
      </div>

      {loading && issues.length === 0 && <Spinner />}
      {!loading && issues.length === 0 && <EmptyState title={S.issues.noIssues} />}

      <ul className="flex flex-col gap-2">
        {issues.map((issue) => (
          <li key={issue.id} className="flex items-center gap-2 border-b border-border pb-2
                                        last:border-b-0">
            <Link to={`/issues/${issue.id}`} className="flex-1 truncate text-primary">
              {issue.title}
            </Link>
            <Badge className={`bg-surface-hover ${PRIORITY_CLASS[issue.priority]}`}>
              {ISSUE_PRIORITY_LABELS[issue.priority]}
            </Badge>
            <Badge className={`bg-surface-hover ${STATUS_CLASS[issue.status]}`}>
              {ISSUE_STATUS_LABELS[issue.status]}
            </Badge>
            <span className="text-xs text-text-muted">{formatDate(issue.reportedAt)}</span>
          </li>
        ))}
      </ul>

      <IssueDialog
        open={creating}
        onOpenChange={setCreating}
        contactId={contact.id}
        onCreated={() => refetch()}
      />
    </Card>
  );
}
